import { type SupplierListItem } from '@choco-admin/types';
import { Button, Card, Switch } from '@heroui/react';
import React, { useState } from 'react';

import { AlertCard } from '../../../../../../components/AlertCard';

import { PromotionDetailModal, PromotionTable } from './components';
import { useVendorPromotionList } from './hooks/useVendorPromotionList';
import { type PromotionRow } from './types';

interface PromotionsTabProps {
  supplier?: SupplierListItem;
}

export const PromotionsTab: React.FC<PromotionsTabProps> = ({ supplier }) => {
  const [activeOnly, setActiveOnly] = useState(true);
  const [selectedPromotion, setSelectedPromotion] =
    useState<PromotionRow | null>(null);

  const {
    promotions,
    isLoading,
    isLoadingMore,
    errorMessage,
    loadMoreErrorMessage,
    hasNextPage,
    loadMore,
    replacePromotion,
  } = useVendorPromotionList({
    vendorId: supplier?.id,
    activeOnly,
  });

  if (!supplier) {
    return (
      <AlertCard
        type="info"
        title="Create distributor first"
        message="Please save the distributor to see its promotions."
      />
    );
  }

  return (
    <Card className="p-8 rounded-xl">
      <div className="space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold mb-1">Promotions</h3>
            <p className="text-sm text-default-500">
              This distributor&apos;s promotions. The display copy of a
              promotion can be overridden here.
            </p>
          </div>
          <Switch
            isSelected={activeOnly}
            onValueChange={setActiveOnly}
            size="sm"
          >
            Active only
          </Switch>
        </div>

        {errorMessage ? (
          <AlertCard
            type="error"
            title="Could not load promotions"
            message={errorMessage}
          />
        ) : (
          <>
            <PromotionTable
              promotions={promotions}
              isLoading={isLoading}
              onSelectPromotion={setSelectedPromotion}
            />

            {loadMoreErrorMessage && (
              <AlertCard
                type="error"
                title="Could not load more promotions"
                message={loadMoreErrorMessage}
              />
            )}

            {hasNextPage && (
              <div className="flex justify-center">
                <Button
                  variant="flat"
                  color="default"
                  onPress={loadMore}
                  isLoading={isLoadingMore}
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}

        <PromotionDetailModal
          promotion={selectedPromotion}
          vendorId={supplier.id}
          onClose={() => setSelectedPromotion(null)}
          onSaved={(promotion) => {
            replacePromotion(promotion);
            // A save that lands after the operator opened another promotion
            // updates the list row only, so the open modal keeps its drafts.
            setSelectedPromotion((current) =>
              current?.id === promotion.id ? promotion : current
            );
          }}
        />
      </div>
    </Card>
  );
};
